import React from 'react';
import {
	Card,
	Col,
	Row,
} from 'antd';
import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CentralStoreProvider } from '@client/screens/estimates/details/context/CentralStoreContext';
import EditableCellRedux from '@client/components/EditableTableRedux/EditableCellRedux';
import type { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import AsyncEditableTable from '@client/components/EditableTableRedux/AsyncEditableTable';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import styles from './ItineraryTab.module.css';
import { useItinerary } from './ItineraryProvider';
import ItineraryControls from './components/ItineraryControls';
import ItineraryCargoDetailsModal from './ItineraryCargoDetailsModal/ItineraryCargoDetailsModal';
import {
	ACTUAL_DATE_REGEX,
	CANAL_TRANSIT_REGEX,
	extractBooleanFromKey,
} from './utils/helpers';

const TableRow = (tableRowProps: any) => {
	const key = tableRowProps['data-row-key'];

	const isCanalTransit = extractBooleanFromKey(key, CANAL_TRANSIT_REGEX);
	const isActualDate = extractBooleanFromKey(key, ACTUAL_DATE_REGEX);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: key,
		disabled: isCanalTransit || isActualDate,
	});

	const style: React.CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition,
		cursor: 'move',
		...(isDragging ? { position: 'relative', zIndex: 9999, backgroundColor: 'white' } : {}),
	};

	return (
		<tr
			key={key}
			{...tableRowProps}
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
		/>
	);
};

const extractRowAttributes = (v: string) => {
	const regex = /[?&]id=(\d+)/;
	const id = v.match(regex)?.[1];

	return {
		id: Number(id),
	};
};

const Itinerary = () => {
	const {
		movePortCall,
		onUpdateRob,
		renderKey,
		columns,
		editingCargoId,
		editingCargoPortId,
		setEditingCargoPortId,
		portCalls,
		viewMode,
		hasPendingRobChanges,
		saveRobChanges,
	} = useItinerary();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 1,
			},
		}),
	);

	const onDragEnd = async ({ active, over }: DragEndEvent) => {
		if (over == null) {
			return;
		}

		const { id: overId } = extractRowAttributes(over.id.toString());
		const { id: activeId } = extractRowAttributes(active.id.toString());

		await movePortCall(activeId, overId);
	};

	const handleRobUpdate = async (key: string, value: number, row: ItineraryPortCallDto) => {
		await onUpdateRob(key, row.id, value);
	};

	const button = useSaveButton({
		onSave: () => saveRobChanges(),
		visible: viewMode === 'bunker',
		loading: false,
		hasPendingChanges: hasPendingRobChanges,
	});

	return (
		<Row>
			<Col span={24}>
				<Row align="middle">
					<Col span={24}>
						<Card size="small" className={styles.itineraryControls}>
							<ItineraryControls saveButton={button}/>
						</Card>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<DndContext
					sensors={sensors}
					modifiers={[restrictToVerticalAxis]}
					onDragEnd={onDragEnd}
				>
					<SortableContext
						items={portCalls.map((i) => i.key)}
						strategy={verticalListSortingStrategy}
					>
						<AsyncEditableTable
							key={renderKey}
							className={styles.itineraryTable}
							components={{
								body: {
									row: TableRow,
									cell: EditableCellRedux,
								},
							}}
							onCellChange={handleRobUpdate}
							dataSource={portCalls}
							rowKey="key"
							size="small"
							pagination={false}
							columns={columns}
						/>
					</SortableContext>
				</DndContext>
			</Col>
			<CentralStoreProvider>
				{(editingCargoPortId && editingCargoId) && (
					<ItineraryCargoDetailsModal
						key={editingCargoPortId ?? 'itinerary-port-call-modal'}
						cargoId={editingCargoId}
						cargoPortId={editingCargoPortId}
						setEditingCargoPortId={setEditingCargoPortId}
						visible={editingCargoPortId != null}
					/>
				)}
			</CentralStoreProvider>
		</Row>
	);
};

export default Itinerary;
